export interface WordNuance {
  word: string;
  title: string;
  summary: string;
  distinctions: Array<{
    term: string;
    translation: string;
    explanation: string;
    example?: string;
  }>;
  commonUsages: Array<{
    phrase: string;
    translation: string;
    context: string;
  }>;
}

export const wordNuancesData: Record<string, WordNuance> = {
  stray: {
    word: 'stray',
    title: 'Stray vs. Vira-lata & Beyond Animals',
    summary:
      'Em português, costuma-se traduzir "stray" como "vira-lata", mas tecnicamente têm significados diferentes! Além disso, "stray" é amplamente usado para objetos perdidos, fios soltos e ações de se desviar.',
    distinctions: [
      {
        term: 'Stray (dog / cat)',
        translation: 'Animal de rua / perdido / abandonado',
        explanation:
          'Indica um animal sem lar ou perdido na rua, independente de ter raça ou não. Um Golden Retriever perdido na rua é um "stray".',
        example: 'We rescued a friendly stray dog outside our building.'
      },
      {
        term: 'Mutt / Mongrel / Mixed-breed',
        translation: 'Vira-lata / SRD (Sem Raça Definida)',
        explanation:
          'Indica a ausência de raça pura. Um vira-lata adotado dormindo no seu sofá é um "mutt", não um "stray".',
        example: 'My dog is a lovely mutt from the local shelter.'
      }
    ],
    commonUsages: [
      {
        phrase: 'Stray bullet',
        translation: 'Bala perdida',
        context: 'Objetos / perigo acidental'
      },
      {
        phrase: 'Stray hair / thread',
        translation: 'Fio de cabelo solto / fio puxado',
        context: 'Coisas avulsas ou fora do lugar'
      },
      {
        phrase: 'Stray thought',
        translation: 'Pensamento disperso / aleatório',
        context: 'Ideias avulsas'
      },
      {
        phrase: 'To stray from the path / topic',
        translation: 'Desviar-se do caminho / do assunto',
        context: 'Verbo: afastar-se do rumo correto'
      }
    ]
  },
  mutt: {
    word: 'mutt',
    title: 'Mutt vs. Stray',
    summary:
      '"Mutt" (ou "mongrel") refere-se à genética do cão (vira-lata / sem raça definida), ao contrário de "stray", que se refere à condição de estar na rua ou perdido.',
    distinctions: [
      {
        term: 'Mutt',
        translation: 'Vira-lata',
        explanation: 'Cão sem raça definida (SRD).',
        example: 'He is the smartest mutt I have ever seen.'
      },
      {
        term: 'Stray',
        translation: 'Cão de rua / abandonado',
        explanation: 'Cão que vive na rua ou está perdido.',
        example: 'The animal control picked up a stray near the park.'
      }
    ],
    commonUsages: [
      {
        phrase: 'Mixed-breed dog',
        translation: 'Cão mestiço / SRD',
        context: 'Termo mais formal para mutt'
      }
    ]
  },
  strain: {
    word: 'strain',
    title: 'Strain: Tensão, Esforço, Distensão, Cepa ou Coar?',
    summary:
      'Em inglês, "strain" tem vários significados cruciais dependendo de atuar como substantivo ou verbo: desde tensão/sobrecarga (física, mental ou estrutural), distensão muscular (estiramento de músculo/tendão), cepa/linhagem de vírus, até os verbos forçar algo ao limite ou coar/filtrar na cozinha.',
    distinctions: [
      {
        term: 'Strain (pressão / sobrecarga - Noun)',
        translation: 'Tensão / Sobrecarga / Pressão',
        explanation:
          'Pressão extrema colocada sobre um corpo, mente, finanças ou relacionamento.',
        example: 'The pandemic put immense strain on the healthcare system.'
      },
      {
        term: 'Muscle strain (saúde - Noun)',
        translation: 'Distensão / Estiramento muscular',
        explanation:
          'Lesão por esticar um músculo ou tendão além do limite (diferente de "sprain", que é torção/entorse em ligamentos).',
        example: 'He suffered a severe hamstring strain during the sprint.'
      },
      {
        term: 'Strain (biologia / vírus - Noun)',
        translation: 'Cepa / Linhagem / Subtipo',
        explanation:
          'Variante genética ou subtipo de um vírus, bactéria ou fungo.',
        example: 'Scientists discovered a new, highly contagious strain of the virus.'
      },
      {
        term: 'To strain (culinária / líquidos - Verb)',
        translation: 'Coar / Peneirar / Filtrar',
        explanation:
          'Separar sólidos de líquidos com um coador ou peneira (strainer).',
        example: 'Strain the cooked pasta and return it to the pot.'
      },
      {
        term: 'To strain (esforço físico ou sensorial - Verb)',
        translation: 'Forçar / Esforçar-se ao máximo',
        explanation:
          'Exigir o máximo da visão, audição, voz ou capacidade muscular.',
        example: 'Sitting in back, I had to strain to hear what the speaker said.'
      }
    ],
    commonUsages: [
      {
        phrase: 'Under strain',
        translation: 'Sob grande pressão / sobrecarga',
        context: 'Relacionamentos, finanças, estruturas e saúde mental'
      },
      {
        phrase: 'Muscle strain',
        translation: 'Distensão muscular',
        context: 'Esportes e atividades físicas'
      },
      {
        phrase: 'Strain one’s eyes / voice',
        translation: 'Forçar a vista / forçar a voz',
        context: 'Esforço corporal desgastante'
      },
      {
        phrase: 'Repetitive strain injury (RSI)',
        translation: 'Lesão por esforço repetitivo (LER)',
        context: 'Medicina do trabalho e ergonomia'
      },
      {
        phrase: 'Strains of music',
        translation: 'Acordes / notas suaves de melodia',
        context: 'Música e literatura'
      }
    ]
  },
  novel: {
    word: 'novel',
    title: 'Novel: Substantivo (Romance) vs. Adjetivo (Novo, Inédito, Original)',
    summary:
      'Como substantivo, "novel" significa "romance" (um livro longo de ficção — cuidado com o falso cognato "novela" de TV, que em inglês é "soap opera" ou "telenovela"). Já como adjetivo, "novel" significa "novo, inédito, original, inovador" — algo diferente de tudo que já foi visto antes.',
    distinctions: [
      {
        term: 'Novel (Substantivo - Noun)',
        translation: 'Romance (obra literária de ficção)',
        explanation:
          'Livro de narrativa longa de ficção em prosa (ex.: romance policial, histórico ou de época). Não confunda com novela de TV (soap opera).',
        example: 'She just published her first historical novel.'
      },
      {
        term: 'Novel (Adjetivo - Adjective)',
        translation: 'Novo / Inédito / Original / Inovador',
        explanation:
          'Descreve ideias, métodos, descobertas ou coisas novas, originais e interessantes por serem inéditas.',
        example: 'The scientists proposed a novel approach to solve the energy crisis.'
      }
    ],
    commonUsages: [
      {
        phrase: 'A novel idea / approach',
        translation: 'Uma ideia original / abordagem inédita',
        context: 'Adjetivo: inovação, negócios e ciência'
      },
      {
        phrase: 'Novel virus / strain',
        translation: 'Novo vírus / cepa inédita',
        context: 'Adjetivo: medicina e ciência (recém-descoberto)'
      },
      {
        phrase: 'Graphic novel',
        translation: 'Romance gráfico / HQ longa',
        context: 'Substantivo: literatura e quadrinhos'
      },
      {
        phrase: 'Mystery / Romance novel',
        translation: 'Livro de mistério / Livro de romance',
        context: 'Substantivo: gêneros literários'
      }
    ]
  },
  sourdough: {
    word: 'sourdough',
    title: 'Sourdough: Pão de Fermentação Natural vs. Garimpeiro?',
    summary:
      'Em inglês moderno, "sourdough" refere-se quase sempre ao pão artesanal de fermentação natural ou à própria massa/fermento natural (levain). Porém, em contextos históricos e dicionários americanos antigos, "sourdough" também era uma gíria famosa para os garimpeiros da Corrida do Ouro da Califórnia (1849) e do Alasca/Yukon, que dormiam com o fermento no corpo para que ele não congelasse no inverno!',
    distinctions: [
      {
        term: 'Sourdough bread (Culinária moderna)',
        translation: 'Pão de fermentação natural',
        explanation:
          'Pão rústico artesanal feito com fermentação lenta por leveduras selvagens e lactobacilos, sem fermento biológico comercial.',
        example: 'I bought a fresh loaf of sourdough bread from the artisan bakery.'
      },
      {
        term: 'Sourdough starter / Levain',
        translation: 'Fermento natural / Massa madre / Levain',
        explanation:
          'A mistura viva de farinha e água cultivada que fermenta a massa do pão.',
        example: 'She feeds her sourdough starter every morning.'
      },
      {
        term: 'Sourdough (Gíria histórica / Slang)',
        translation: 'Garimpeiro / Veterano do Alasca ou da Corrida do Ouro',
        explanation:
          'Apelido dado aos mineradores e desbravadores de 1849 na Califórnia e no Alasca. Por isso alguns tradutores e APIs automáticas mostram "garimpeiro".',
        example: 'The old sourdoughs spent years searching for gold in the Klondike.'
      }
    ],
    commonUsages: [
      {
        phrase: 'A loaf of sourdough',
        translation: 'Um pão de fermentação natural',
        context: 'Culinária e padaria'
      },
      {
        phrase: 'Sourdough starter',
        translation: 'Isca de fermento natural / Levain',
        context: 'Panificação artesanal'
      },
      {
        phrase: 'Sourdough pizza crust',
        translation: 'Massa de pizza de fermentação natural',
        context: 'Gastronomia'
      },
      {
        phrase: 'Old sourdough',
        translation: 'Pioneiro veterano do Alasca / Garimpeiro',
        context: 'História e literatura americana'
      }
    ]
  },
  tackle: {
    word: 'tackle',
    title: 'Tackle: Enfrentar / Resolver um Problema vs. Equipamento & Carrinho no Esporte',
    summary:
      'No dia a dia e nos negócios (Business English), "to tackle" é muito usado para expressar "enfrentar, resolver, lidar com ou colocar a mão na massa" em um problema, projeto ou desafio. Nos esportes (como futebol, futebol americano e rúgbi), significa "desarmar, derrubar ou dar um carrinho". Já como substantivo, refere-se a equipamentos ou apetrechos (especialmente de pesca: "fishing tackle").',
    distinctions: [
      {
        term: 'To tackle a problem / challenge (Verbo - Mais comum)',
        translation: 'Enfrentar / Lidar com / Resolver / Encarar',
        explanation:
          'Fazer um esforço determinado e ativo para resolver uma situação difícil, tarefa ou desafio.',
        example: 'Let’s tackle the most difficult issues first before the meeting ends.'
      },
      {
        term: 'To tackle (Verbo nos esportes) / A tackle (Substantivo)',
        translation: 'Desarmar / Derrubar / Dar um carrinho',
        explanation:
          'Interromper a jogada do oponente tirando a bola ou levando-o ao chão.',
        example: 'The defender made a brilliant tackle to save the match.'
      },
      {
        term: 'Tackle (Substantivo - Pesca e Náutica)',
        translation: 'Equipamento / Apetrechos / Tralha de pesca',
        explanation:
          'Conjunto de ferramentas ou equipamentos (especialmente vara, carretilha, anzol, etc.).',
        example: 'He bought new fishing tackle for his weekend trip.'
      }
    ],
    commonUsages: [
      {
        phrase: 'Tackle a problem / issue',
        translation: 'Enfrentar / resolver um problema',
        context: 'Ambiente de trabalho e dia a dia'
      },
      {
        phrase: 'Tackle a project / task',
        translation: 'Pegar firme em um projeto / encarar uma tarefa',
        context: 'Produtividade e negócios'
      },
      {
        phrase: 'Tackle crime / inflation',
        translation: 'Combater o crime / combater a inflação',
        context: 'Notícias, economia e governo'
      },
      {
        phrase: 'Fishing tackle / tackle box',
        translation: 'Tralha de pesca / caixa de apetrechos de pesca',
        context: 'Hobbies e esportes'
      }
    ]
  },
  flare: {
    word: 'flare',
    title: 'Flare: Clarão Repentino / Labareda / Sinalizador vs. Alargamento',
    summary:
      'Em inglês, o sentido principal e mais frequente de "flare" é uma luz brilhante ou chama intensa e repentina (como um clarão, labareda ou sinalizador de resgate). No entanto, também é usado para descrever o formato que se alarga para fora (como calças boca de sino / flare ou tubos alargados) e crises/pioras repentinas de saúde ou discussões ("flare-up").',
    distinctions: [
      {
        term: 'A sudden bright light / flame (Substantivo & Verbo - Sentido Principal)',
        translation: 'Clarão repentino / Labareda / Chamejar / Emitir clarão',
        explanation:
          'Uma emissão súbita e intensa de luz brilhante ou fogo.',
        example: 'A sudden flare of light illuminated the dark room.'
      },
      {
        term: 'Distress flare / Flare gun (Segurança & Resgate)',
        translation: 'Sinalizador luminoso de emergência',
        explanation:
          'Artefato pirotécnico disparado para pedir socorro no mar ou na mata.',
        example: 'The sailors fired a distress flare into the night sky.'
      },
      {
        term: 'Solar flare & Lens flare (Ciência & Fotografia)',
        translation: 'Erupção solar / Clarão (reflexo) de luz na lente',
        explanation:
          'Explosão de radiação na superfície do sol (solar flare) ou feixe de luz refletido na lente da câmera (lens flare).',
        example: 'The telescope captured a massive solar flare erupting from the sun.'
      },
      {
        term: 'To flare / Flared (Formato & Moda)',
        translation: 'Alargar-se / Abrir para fora / Boca de sino',
        explanation:
          'Abrir-se progressivamente em largura (ex.: calça flare, narinas dilatadas ou cano alargado).',
        example: 'She wore 70s style flared jeans.'
      },
      {
        term: 'Flare-up (Saúde & Conflitos)',
        translation: 'Crise súbita / Piora repentina / Erupção de raiva',
        explanation:
          'Retorno ou agravamento súbito de sintomas de dor, inflamação ou tensão.',
        example: 'He suffered a painful flare-up of arthritis in his knee.'
      }
    ],
    commonUsages: [
      {
        phrase: 'Solar flare',
        translation: 'Erupção solar / explosão solar',
        context: 'Astronomia e notícias'
      },
      {
        phrase: 'Distress flare / flare gun',
        translation: 'Sinalizador de emergência / pistola sinalizadora',
        context: 'Navegação, aviação e resgate'
      },
      {
        phrase: 'Lens flare',
        translation: 'Clarão / reflexo de luz na lente',
        context: 'Fotografia, cinema e design'
      },
      {
        phrase: 'Flared jeans / pants',
        translation: 'Calça boca de sino (estilo flare)',
        context: 'Moda e vestuário'
      },
      {
        phrase: 'Flare-up of symptoms',
        translation: 'Crise / piora repentina de sintomas',
        context: 'Saúde e medicina'
      }
    ]
  },
  bearing: {
    word: 'bearing',
    title: 'Bearing: Relevância, Rolamento, Orientação ou Postura?',
    summary:
      'Em inglês, "bearing" tem múltiplos significados cruciais e não tem qualquer relação com o animal "urso" (bear)! Como substantivo, significa relevância/relação ("have a bearing on"), rolamento/mancal mecânico, orientação/rumos ("get one\'s bearings") e postura/porte corporal. Como adjetivo, significa que sustenta carga ("bearing wall"). Como verbo, deriva do verbo "to bear" (suportar, carregar, aguentar, produzir).',
    distinctions: [
      {
        term: 'Have a bearing on (relevance / connection)',
        translation: 'Relação / Relevância / Influência',
        explanation:
          'Usado para indicar que algo tem impacto direto, relevância ou conexão com um assunto ou resultado.',
        example: 'His previous experience had a direct bearing on his promotion.'
      },
      {
        term: "Get one's bearings (orientation / direction)",
        translation: 'Situar-se / Orientar-se / Encontrar o rumo',
        explanation:
          'Determinar a própria posição física em um lugar desconhecido, ou compreender uma situação nova antes de agir.',
        example: 'Let me look at the map and get my bearings.'
      },
      {
        term: 'Bearing (mechanical engineering)',
        translation: 'Rolamento / Mancal',
        explanation:
          'Dispositivo mecânico que apoia um eixo ou reduz o atrito entre partes móveis (ex: ball bearing = rolamento de esferas).',
        example: 'The mechanic replaced the front wheel bearing of the car.'
      },
      {
        term: 'Bearing (demeanor / posture)',
        translation: 'Porte / Postura / Conduta',
        explanation:
          'A postura corporal, porte elegante ou a maneira nobre com que alguém se comporta e se apresenta.',
        example: 'She entered the courtroom with a calm and dignified bearing.'
      },
      {
        term: 'Bearing wall / structure (architecture)',
        translation: 'Parede mestra / Suporte de carga',
        explanation:
          'Elemento estrutural de uma construção projetado para sustentar o peso e a carga de pavimentos superiores ou vigas.',
        example: 'Do not remove that column; it is a load-bearing structure.'
      }
    ],
    commonUsages: [
      {
        phrase: 'Have no bearing on...',
        translation: 'Não ter relação / influência com...',
        context: 'Relevância e tomada de decisão'
      },
      {
        phrase: "Get one's bearings",
        translation: 'Situar-se / orientar-se',
        context: 'Navegação e adaptação'
      },
      {
        phrase: "Lose one's bearings",
        translation: 'Desorientar-se / perder o rumo',
        context: 'Perda de direção física ou mental'
      },
      {
        phrase: 'Ball bearing',
        translation: 'Rolamento de esferas',
        context: 'Mecânica e indústria'
      },
      {
        phrase: 'Load-bearing wall',
        translation: 'Parede de sustentação / parede mestra',
        context: 'Construção civil e arquitetura'
      },
      {
        phrase: 'Dignified bearing',
        translation: 'Porte distinto / nobre',
        context: 'Postura e comportamento'
      }
    ]
  }
};

export const partOfSpeechTranslationsMap: Record<string, Record<string, string[]>> = {
  bearing: {
    noun: [
      'Relação / Relevância (have a bearing on)',
      'Rolamento / Mancal (mecânica)',
      'Orientação / Rumos (bearings)',
      'Porte / Postura / Conduta'
    ],
    adjective: [
      'De suporte / Portante (bearing wall)',
      'Que traz / Producente'
    ],
    verb: [
      'Particípio do verbo bear (suportando, carregando, aguentando)'
    ]
  },
  flare: {
    noun: [
      'Clarão (repentino)',
      'Labareda / Chama forte',
      'Sinalizador luminoso (emergência)',
      'Alargamento (boca de sino)'
    ],
    verb: [
      'Emitir clarão / Chamejar / Brilhar subitamente',
      'Alargar-se / Abrir para fora',
      'Intensificar-se / Explodir (flare up)'
    ]
  },
  tackle: {
    verb: [
      'Enfrentar / Lidar com / Resolver (um problema ou tarefa)',
      'Derrubar / Desarmar (esportes)'
    ],
    noun: [
      'Equipamento / Apetrechos (especialmente de pesca)',
      'Desarme / Carrinho (no esporte)'
    ]
  },
  sourdough: {
    noun: [
      'Pão de fermentação natural',
      'Massa azeda / Levain (fermento natural)',
      'Garimpeiro (gíria histórica da Corrida do Ouro)'
    ]
  },
  novel: {
    noun: ['Romance (livro de ficção)'],
    adjective: ['Novo', 'Inédito', 'Original', 'Inovador']
  },
  strain: {
    noun: ['Tensão', 'Sobrecarga', 'Distensão muscular', 'Cepa / Linhagem'],
    verb: ['Forçar', 'Esforçar-se', 'Coar / Filtrar']
  },
  stray: {
    noun: ['Animal de rua / sem dono', 'Animal perdido'],
    verb: ['Desviar-se', 'Afastar-se', 'Perder-se'],
    adjective: ['Perdido', 'Avulso', 'Solto']
  }
};

export const wordTranslationsMap: Record<string, string> = {
  bearing: 'Relação, Relevância / Rolamento / Orientação, Porte (subst.) • De suporte, Portante (adj.)',
  flare: 'Clarão (repentino) / Labareda / Sinalizador (subst.) • Chamejar / Alargar-se (verbo)',
  tackle: 'Enfrentar / Lidar com / Resolver (verbo) • Equipamento / Apetrechos (subst.)',
  sourdough: 'Pão de fermentação natural / Levain (massa madre)',
  novel: 'Romance (subst.) / Novo, Inédito, Original (adj.)',
  strain: 'Tensão / Esforço / Distensão / Cepa / Coar',
  stray: 'Animal de rua / Perdido / Desviar-se'
};

export function getWordTranslationOverride(word: string): string | null {
  if (!word) return null;
  const key = word.trim().toLowerCase();
  return wordTranslationsMap[key] || null;
}

export function getPartOfSpeechTranslations(word: string): Record<string, string[]> | null {
  if (!word) return null;
  const key = word.trim().toLowerCase();
  return partOfSpeechTranslationsMap[key] || null;
}

export function getWordNuance(word: string): WordNuance | null {
  if (!word) return null;
  const key = word.trim().toLowerCase();
  return wordNuancesData[key] || null;
}
